import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Paper, Typography } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router-dom';
import { formatDateTime, getFormatConfig } from '../../../utils/global';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
} from '../../../theme/harmonateStyles';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';
const getUserId = () => localStorage.getItem('user_id') || null;

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    throw new Error(`Request failed (${response.status})`);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const transactionPath = (row) => {
  if (row.module === 'AP_INVOICE' && row.transactionId) {
    return `/treasury-payment/invoices?invoiceId=${row.transactionId}`;
  }
  return null;
};

const NotificationCenter = () => {
  const navigate = useNavigate();
  const tenantId = getTenantId();
  const userId = getUserId();
  const config = getFormatConfig();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInbox = useCallback(async () => {
    if (!tenantId || !userId) {
      setRows([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await apiFetch(`conductor/notifications?tenant_id=${tenantId}&user_id=${userId}`);
      setRows((Array.isArray(data) ? data : []).map((item) => ({
        id: item.notificationId,
        ...item,
      })));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, userId]);

  useEffect(() => {
    fetchInbox();
  }, [fetchInbox]);

  const markAllRead = async () => {
    if (!tenantId || !userId) return;
    await apiFetch(`conductor/notifications/read-all?tenant_id=${tenantId}&user_id=${userId}`, { method: 'PUT' });
    fetchInbox();
  };

  const openTransaction = async (row) => {
    if (!row?.id) return;
    await apiFetch(
      `conductor/notifications/${row.id}/read?tenant_id=${tenantId}&user_id=${userId}`,
      { method: 'PUT' }
    ).catch(() => null);
    const path = transactionPath(row);
    if (path) navigate(path);
    else fetchInbox();
  };

  const columns = useMemo(() => [
    {
      field: 'createdAt',
      headerName: 'Date/Time',
      width: 180,
      valueFormatter: (p) => (p.value ? formatDateTime(p.value, config.DateTimeFormat) : ''),
    },
    { field: 'module', headerName: 'Module', width: 130 },
    { field: 'transactionNumber', headerName: 'Transaction Number', width: 160 },
    { field: 'subject', headerName: 'Subject', flex: 1, minWidth: 200 },
    { field: 'message', headerName: 'Message', flex: 1.2, minWidth: 240 },
    { field: 'assignedUserName', headerName: 'Assigned User', width: 150 },
    { field: 'notificationEmail', headerName: 'Email ID', width: 180 },
    { field: 'notificationType', headerName: 'Type', width: 110 },
    {
      field: 'isRead',
      headerName: 'Read',
      width: 110,
      renderCell: (params) => (
        <Chip size="small" label={params.value ? 'Read' : 'Unread'} color={params.value ? 'default' : 'success'} />
      ),
    },
    { field: 'emailDeliveryStatus', headerName: 'Email Status', width: 130 },
    { field: 'transactionStatus', headerName: 'Txn Status', width: 140 },
  ], [config.DateTimeFormat]);

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>Notification Center</Typography>
        <Button variant="contained" onClick={markAllRead} sx={harmonateProceedButtonSx}>Mark all as read</Button>
      </Box>
      <Paper sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DataGridPro
          rows={rows}
          columns={columns}
          loading={loading}
          disableMultipleRowSelection
          onRowClick={(params) => openTransaction(params.row)}
          sx={{
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' },
          }}
        />
      </Paper>
    </Box>
  );
};

export default NotificationCenter;
